/* eslint-disable @typescript-eslint/member-ordering */
import { computed, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AUTH_CONFIG } from '../constants/auth-constants';
import { AuthError, AuthSession, TokenStatus } from '../models/auth.model';

interface AuthState {
  session: AuthSession | null;
  tokenStatus: TokenStatus;
  isAuthenticated: boolean;
  sessionExpiry: number | null;
  refreshInProgress: boolean;
  isLoggingOut: boolean;
  lastRefreshError: AuthError | null;
}

const INITIAL_STATE: AuthState = {
  session: null,
  tokenStatus: 'invalid',
  isAuthenticated: false,
  sessionExpiry: null,
  refreshInProgress: false,
  isLoggingOut: false,
  lastRefreshError: null,
};

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly _session = signal<AuthSession | null>(INITIAL_STATE.session);
  private readonly _tokenStatus = signal<TokenStatus>(
    INITIAL_STATE.tokenStatus
  );
  private readonly _isAuthenticated = signal<boolean>(
    INITIAL_STATE.isAuthenticated
  );
  private readonly _sessionExpiry = signal<number | null>(
    INITIAL_STATE.sessionExpiry
  );
  private readonly _refreshInProgress = signal<boolean>(
    INITIAL_STATE.refreshInProgress
  );
  private readonly _isLoggingOut = signal<boolean>(INITIAL_STATE.isLoggingOut);
  private readonly _lastRefreshError = signal<AuthError | null>(
    INITIAL_STATE.lastRefreshError
  );

  private readonly _logoutSubject = new Subject<void>();

  public readonly $session = this._session.asReadonly();
  public readonly $tokenStatus = this._tokenStatus.asReadonly();

  public readonly $isAuthenticated = this._isAuthenticated.asReadonly();
  public readonly $sessionExpiry = this._sessionExpiry.asReadonly();
  public readonly $refreshInProgress = this._refreshInProgress.asReadonly();
  public readonly $isLoggingOut = this._isLoggingOut.asReadonly();

  public readonly $lastRefreshError = this._lastRefreshError.asReadonly();

  public readonly $isTokenValid = computed(() =>
    ['valid', 'refreshing'].includes(this._tokenStatus())
  );

  public readonly $isTokenNearExpiry = computed(() => {
    const expiry = this._sessionExpiry();
    if (!expiry) {
      return false;
    }
    return Date.now() >= expiry - AUTH_CONFIG.TOKEN_NEAR_EXPIRY_THRESHOLD_MS;
  });

  public readonly $canMakeRequests = computed(
    () =>
      this._isAuthenticated() && this.$isTokenValid() && !this._isLoggingOut()
  );

  public readonly logout$: Observable<void> =
    this._logoutSubject.asObservable();

  updateSession(session: AuthSession): void {
    this._session.set(session);
    this._isAuthenticated.set(
      session.isAuthenticated && !!session.tokens?.accessToken
    );
    const expiry = this._calculateSessionExpiry(session);
    this._sessionExpiry.set(expiry);
    this._tokenStatus.set(
      session.tokens?.accessToken && (!expiry || Date.now() < expiry)
        ? 'valid'
        : 'invalid'
    );

    if (session.tokens?.accessToken) {
      this._lastRefreshError.set(null);
    }
  }

  setTokenStatus(status: TokenStatus): void {
    this._tokenStatus.set(status);
  }

  setRefreshInProgress(value: boolean): void {
    this._refreshInProgress.set(value);
  }

  setIsLoggingOut(value: boolean): void {
    this._isLoggingOut.set(value);
  }

  setLastRefreshError(error: AuthError | null): void {
    this._lastRefreshError.set(error);
  }

  getSession(): AuthSession {
    const session = this._session();
    if (!session) {
      throw new Error('No session available');
    }
    return session;
  }

  getSessionOrNull(): AuthSession | null {
    return this._session();
  }

  resetState(): void {
    this._session.set(INITIAL_STATE.session);
    this._tokenStatus.set(INITIAL_STATE.tokenStatus);
    this._isAuthenticated.set(INITIAL_STATE.isAuthenticated);
    this._sessionExpiry.set(INITIAL_STATE.sessionExpiry);
    this._refreshInProgress.set(INITIAL_STATE.refreshInProgress);
    this._isLoggingOut.set(INITIAL_STATE.isLoggingOut);
    this._lastRefreshError.set(INITIAL_STATE.lastRefreshError);
  }

  emitLogout(): void {
    this._logoutSubject.next();
  }

  private _calculateSessionExpiry(session: AuthSession): number | null {
    const exp = session.tokens?.idToken?.payload?.exp;
    if (typeof exp === 'number') {
      return exp * 1000;
    }
    return null;
  }
}
